import _ from "lodash";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MasterSelect } from "../common/MasterSelect";

import { FormGrid, FormGroup, FormRow, Spacer } from "@/components/common";
import { FilterConfigGroup } from "@/components/common/SearchCondition";
import { SearchConditionConfigModal } from "@/components/common/SearchCondition/SearchConditionConfigModal";
import { SearchConditionDisplayButton } from "@/components/common/SearchCondition/SearchConditionDisplayButton";
import { SettingIconButton } from "@/components/common/SettingIconButton";
import { useSearchConditionConfig } from "@/hooks/useSearchConditionConfig";
import { FilterConfig } from "@/models/filterConfig";
import { ConstructionMasters } from "@/models/masters";
import { SearchConditionConfigDefinition, ScreenName } from "@/models/screenConfig";
import { SummarySearchKey, SummarySearchKeyType, SummarySearchParams } from "@/models/summary";

type Props = {
  masters: ConstructionMasters;
  areaIds: number[];
  deviceIds: number[];
  categoryIds: number[];
  facilityManagementIds: number[];
  constructionManagementIds: number[];
  primaryChargeIds: number[];
};

type DispatchProps = {
  changeArea: (value: number[]) => void;
  changeDevice: (value: number[]) => void;
  changeCategory: (value: number[]) => void;
  changeFacilityManagement: (value: number[]) => void;
  changeConstructionManagement: (value: number[]) => void;
  changePrimaryCharge: (value: number[]) => void;
  handleSearch: (option?: { params?: SummarySearchParams }) => void;
  resetSearch: () => void;
  handleReset: () => void;
};

type SearchPanelProps = Props & DispatchProps;

const SearchPanel = forwardRef<HTMLDivElement, SearchPanelProps>(
  (
    {
      masters,
      areaIds,
      deviceIds,
      categoryIds,
      facilityManagementIds,
      constructionManagementIds,
      primaryChargeIds,
      changeArea,
      changeDevice,
      changeCategory,
      changeFacilityManagement,
      changeConstructionManagement,
      changePrimaryCharge,
      handleReset,
      resetSearch,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    // データ取り込み画面の検索条件設定の定義
    const summarySearchConditionDefinitions = useMemo<SearchConditionConfigDefinition<SummarySearchKeyType>[]>(
      () => [
        {
          itemsPerLine: 3,
          items: [
            { key: SummarySearchKey.Area, label: t("area"), required: false },
            { key: SummarySearchKey.Device, label: t("device"), required: false },
            { key: SummarySearchKey.Category, label: t("machines_category"), required: false },
            { key: SummarySearchKey.FacilityManagement, label: t("facility_management"), required: false },
            { key: SummarySearchKey.ConstructionManagement, label: t("construction_management"), required: false },
            { key: SummarySearchKey.PrimaryCharge, label: t("primary_charge"), required: false },
          ],
        },
      ],
      [t]
    );

    // 検索条件設定
    const config = useSearchConditionConfig<SummarySearchKeyType>({
      screenName: ScreenName.SUMMARY,
      requiredKeys: summarySearchConditionDefinitions.flatMap((v) =>
        v.items.filter((item) => item.required).map((item) => item.key)
      ),
    });
    const filterSearchConditionByConfig = (condition?: SummarySearchParams): SummarySearchParams => {
      const params = condition || {
        areaIds,
        deviceIds,
        categoryIds,
        facilityManagementIds,
        constructionManagementIds,
        primaryChargeIds,
      };
      if (!config.map || config.showAllConditions) return params;

      return {
        areaIds: config.map.get(SummarySearchKey.Area) ? params.areaIds : [],
        deviceIds: config.map.get(SummarySearchKey.Device) ? params.deviceIds : [],
        categoryIds: config.map.get(SummarySearchKey.Category) ? params.categoryIds : [],
        facilityManagementIds: config.map.get(SummarySearchKey.FacilityManagement) ? params.facilityManagementIds : [],
        constructionManagementIds: config.map.get(SummarySearchKey.ConstructionManagement)
          ? params.constructionManagementIds
          : [],
        primaryChargeIds: config.map.get(SummarySearchKey.PrimaryCharge) ? params.primaryChargeIds : [],
      };
    };

    // 検索条件保存
    const getCurrentConditionLabels = () => {
      // 現在の条件を保存モーダルに表示するために編集する
      const p = filterSearchConditionByConfig();
      const names: { label: string; values: string[] | null }[] = [
        {
          label: t("area"),
          values: p.areaIds?.map((id) => masters?.areas.find((v) => v.area_id === id)?.area_name),
        },
        {
          label: t("device"),
          values: p.deviceIds?.map((id) => masters?.devices.find((v) => v.device_id === id)?.device_name),
        },
        {
          label: t("machines_category"),
          values: p.categoryIds?.map((id) => masters?.categories.find((v) => v.category_id === id)?.category_name),
        },
        {
          label: t("facility_management"),
          values: p.facilityManagementIds?.map(
            (id) => masters?.facility_managements.find((v) => v.facility_management_id === id)?.facility_management_name
          ),
        },
        {
          label: t("construction_management"),
          values: p.constructionManagementIds?.map(
            (id) =>
              masters?.construction_managements.find((v) => v.construction_management_id === id)
                ?.construction_management_name
          ),
        },
        {
          label: t("primary_charge"),
          values: p.primaryChargeIds?.map(
            (id) => masters?.primary_charges.find((v) => v.primary_charge_id === id)?.primary_charge_name
          ),
        },
      ];

      return names.map((n) => ({ label: n.label, values: _.compact(n.values) })).filter((n) => n.values?.length);
    };
    const currentSearchParams = useMemo<SummarySearchParams>(() => {
      return filterSearchConditionByConfig();
    }, [
      areaIds,
      deviceIds,
      categoryIds,
      facilityManagementIds,
      constructionManagementIds,
      primaryChargeIds,
      config.map,
      config.showAllConditions,
    ]);
    const handleApplyFilterConfig = (filter: FilterConfig<SummarySearchParams>) => {
      // 検索条件設定で表示している検索条件のみ復元する
      const condition = filterSearchConditionByConfig(filter.config);
      changeArea(condition.areaIds);
      changeDevice(condition.deviceIds);
      changeCategory(condition.categoryIds);
      changeFacilityManagement(condition.facilityManagementIds);
      changeConstructionManagement(condition.constructionManagementIds);
      changePrimaryCharge(condition.primaryChargeIds);
    };

    const handleSearch = (e: React.MouseEvent) => {
      e.preventDefault();

      if (config.showAllConditions) {
        props.handleSearch();
      } else {
        // 検索条件設定で表示している検索条件のみ有効とする
        props.handleSearch({ params: filterSearchConditionByConfig() });
      }
    };

    if (!config.map) return <></>;

    return (
      <div ref={ref}>
        <div className="flex justify-between bg-[#e4e4e4] w-[1200px]">
          <FilterConfigGroup
            screenName={ScreenName.SUMMARY}
            getCurrentConditionLabels={getCurrentConditionLabels}
            currentConfig={currentSearchParams}
            applyFilterConfig={handleApplyFilterConfig}
          />
          <SettingIconButton className="w-[32px] p-[8px]" onClick={config.openSettingModal} />
        </div>
        <div className="search-box">
          <div className="grid">
            <FormGrid
              className="grid-cols-3"
              display={config.show([
                SummarySearchKey.Area,
                SummarySearchKey.Device,
                SummarySearchKey.Category,
                SummarySearchKey.FacilityManagement,
                SummarySearchKey.ConstructionManagement,
                SummarySearchKey.PrimaryCharge,
              ])}
            >
              <FormGroup title={t("area")} className="w-230" display={config.show([SummarySearchKey.Area])}>
                <MasterSelect
                  prefix="area"
                  isMulti={true}
                  options={masters.areas}
                  value={areaIds}
                  onChange={changeArea}
                />
              </FormGroup>
              <FormGroup title={t("device")} className="w-260" display={config.show([SummarySearchKey.Device])}>
                <MasterSelect
                  prefix="device"
                  isMulti={true}
                  options={masters.devices}
                  value={deviceIds}
                  onChange={changeDevice}
                />
              </FormGroup>
              <FormGroup
                title={t("machines_category")}
                className="w-290"
                display={config.show([SummarySearchKey.Category])}
              >
                <MasterSelect
                  prefix="category"
                  isMulti={true}
                  options={masters.categories}
                  value={categoryIds}
                  onChange={changeCategory}
                />
              </FormGroup>
              <FormGroup
                title={t("facility_management")}
                className="w-230"
                display={config.show([SummarySearchKey.FacilityManagement])}
              >
                <MasterSelect
                  prefix="facility_management"
                  isMulti={true}
                  options={masters.facility_managements}
                  value={facilityManagementIds}
                  onChange={changeFacilityManagement}
                />
              </FormGroup>
              <FormGroup
                title={t("construction_management")}
                className="w-260"
                display={config.show([SummarySearchKey.ConstructionManagement])}
              >
                <MasterSelect
                  prefix="construction_management"
                  isMulti={true}
                  options={masters.construction_managements}
                  value={constructionManagementIds}
                  onChange={changeConstructionManagement}
                />
              </FormGroup>
              <FormGroup
                title={t("primary_charge")}
                className="w-290"
                display={config.show([SummarySearchKey.PrimaryCharge])}
              >
                <MasterSelect
                  prefix="primary_charge"
                  isMulti={true}
                  options={masters.primary_charges}
                  value={primaryChargeIds}
                  onChange={changePrimaryCharge}
                />
              </FormGroup>
            </FormGrid>
            <FormRow className="justify-between mt-[16px]">
              <Spacer x="210px" />
              <SearchConditionDisplayButton
                isExpanded={config.showAllConditions}
                onChange={config.toggleDisplayOptionalConditions}
              />
              <div className="flex gap-x-4">
                <button
                  className="btn btn-gray"
                  onClick={() => {
                    resetSearch();
                    handleReset();
                  }}
                >
                  {t("reset")}
                </button>
                <button className="btn btn-blue" onClick={handleSearch}>
                  {t("search")}
                </button>
              </div>
            </FormRow>
          </div>
        </div>
        {config.showSettingModal && (
          <SearchConditionConfigModal<SummarySearchKeyType>
            title={t("search_condition_config")}
            configMap={config.map}
            definitions={summarySearchConditionDefinitions}
            onClose={config.closeSettingModal}
            onSubmit={(c) => config.saveSearchItemConfig(c)}
          />
        )}
      </div>
    );
  }
);

SearchPanel.displayName = "SearchPanel";

export default SearchPanel;
